import { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion, useSpring } from 'framer-motion';
import { X } from 'lucide-react';

import { Button } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';

import type { TourStep } from './types';
import { TourIllustration } from './tour-illustrations';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Progress fill eases across steps rather than snapping (D6).
const PROGRESS_SPRING = { stiffness: 300, damping: 30 };

interface TourStepCardProps {
  step: TourStep;
  stepIndex: number;
  stepCount: number;
  /** Roomier width/padding variant for centered teaching beats (D5). */
  large: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export function TourStepCard({
  step,
  stepIndex,
  stepCount,
  large,
  onNext,
  onBack,
  onSkip,
}: TourStepCardProps) {
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === stepCount - 1;
  const reducedMotion = useReducedMotion();

  const containerRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);

  // The card element persists across steps now (the overlay never remounts it),
  // so the progress fill is a spring re-targeted on step change instead of a
  // per-step remount. Its initial value is already correct for the first step.
  const progress = useSpring((stepIndex + 1) / stepCount, PROGRESS_SPRING);
  useEffect(() => {
    const target = (stepIndex + 1) / stepCount;
    if (reducedMotion) progress.jump(target);
    else progress.set(target);
  }, [stepIndex, stepCount, reducedMotion, progress]);

  // Pull focus to the primary button on each step. The overlay's click shield is
  // non-modal, so nothing traps focus on its own — without this, Tab reaches
  // controls behind the shield. preventScroll keeps the focus call from fighting
  // the provider's own smooth scroll of the spotlight target (D5).
  useEffect(() => {
    primaryRef.current?.focus({ preventScroll: true });
  }, [stepIndex]);

  // Keep Tab / Shift+Tab cycling within the card. A native listener (rather than
  // a JSX handler on a non-interactive div) keeps this a focus trap on the
  // role="dialog" container. The trapped controls all live in the persistent
  // chrome/footer, so the trap survives content crossfades unchanged.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={step.title}
      className={cn('flex flex-col gap-3', large ? 'p-6' : 'p-4')}
    >
      {/* Persistent chrome: progress track + counter + skip. */}
      <div className="flex items-center gap-2">
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-border">
          <motion.div
            className="h-full origin-left rounded-full bg-foreground"
            style={{ scaleX: progress }}
            aria-hidden
          />
        </div>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {stepIndex + 1}/{stepCount}
        </span>
        <Button variant="ghost" size="icon-xs" onClick={onSkip} aria-label="Skip tour">
          <X />
        </Button>
      </div>

      {/* Crossfading content zone. aria-live sits on the persistent wrapper so
          screen readers announce each step; the keyed child is what animates. */}
      <div aria-live="polite">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step.id}
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.16, ease: 'easeOut' } }}
            exit={
              reducedMotion
                ? { opacity: 0, transition: { duration: 0.1 } }
                : { opacity: 0, y: -4, transition: { duration: 0.1 } }
            }
            className="flex flex-col gap-3"
          >
            <div className="space-y-1.5">
              <h3
                className={cn(
                  'font-semibold tracking-tight text-foreground',
                  large ? 'text-lg' : 'text-base'
                )}
              >
                {step.title}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{step.body}</p>
            </div>
            {/* `large` is the centered-presentation signal (overlay passes
                large ⇔ centered): detail-resolved steps show their diagram only
                until the real page's spotlight takes over. */}
            {step.illustration && (!step.illustrationOnlyWhenCentered || large) && (
              <TourIllustration id={step.illustration} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Persistent footer. */}
      <div className="flex items-center justify-end gap-2">
        {!isFirst && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            Back
          </Button>
        )}
        <Button ref={primaryRef} size="sm" onClick={onNext}>
          {isLast ? 'Finish' : 'Next'}
        </Button>
      </div>
    </div>
  );
}
